import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useEncuestas } from '../../hooks/useEncuestas';
import { IResultadoRow } from '../../models/encuestas';
import { Loader } from '../../components/Loader';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const COLORS = ['#2a78d6', '#eb6834', '#2ecc71', '#9b59b6', '#f1c40f', '#e74c3c', '#1abc9c', '#34495e'];

const TIPO_LABELS: Record<string, string> = {
  'SINGLE_CHOICE': 'Selección única',
  'MULTIPLE_CHOICE': 'Selección múltiple',
  'TRUE_FALSE': 'Verdadero / Falso',
};

interface IPreguntaResultado {
  pregunta: number;
  texto: string;
  tipo: string;
  opciones: { opcion: number; texto: string; votos: number; color: string }[];
  totalVotos: number;
}

interface ResultadosEncuestaProps {
  idEncuesta: number;
  nombreEncuesta: string;
  onBack: () => void;
}

export const ResultadosEncuesta = ({ idEncuesta, nombreEncuesta, onBack }: ResultadosEncuestaProps) => {
  const { getResultadosEncuesta } = useEncuestas();
  const [rows, setRows] = useState<IResultadoRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getResultadosEncuesta(idEncuesta).then(data => {
      setRows(data);
      setLoading(false);
    });
  }, [idEncuesta]);

  const totalRespuestas = rows.length > 0 ? rows[0].total_Respuestas : 0;

  const preguntas = useMemo<IPreguntaResultado[]>(() => {
    const map = new Map<number, IPreguntaResultado>();
    rows.forEach(r => {
      if (!map.has(r.pregunta)) {
        map.set(r.pregunta, {
          pregunta: r.pregunta,
          texto: r.texto_Pregunta,
          tipo: r.tipo,
          opciones: [],
          totalVotos: 0,
        });
      }
      const p = map.get(r.pregunta)!;
      const colorIdx = p.opciones.length;
      p.opciones.push({
        opcion: r.opcion,
        texto: r.texto_Opcion,
        votos: r.votos,
        color: COLORS[colorIdx % COLORS.length],
      });
      p.totalVotos += r.votos;
    });
    return Array.from(map.values());
  }, [rows]);

  if (loading) return <Box mt={6}><Loader loadingText="Cargando resultados..." /></Box>;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9' }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
        px: 3, pt: 3, pb: 5,
      }}>
        <IconButton onClick={onBack} sx={{ color: '#fff', mb: 1, ml: -1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.3 }}>
          {nombreEncuesta}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          Resultados en tiempo real
        </Typography>
      </Box>

      <Box sx={{ px: 2, mt: -2, pb: 4 }}>
        <Box sx={{
          backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 2,
          boxShadow: '0 4px 16px rgba(1,52,103,0.1)',
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '50%',
            backgroundColor: '#e8f0fb',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PeopleIcon sx={{ color: '#013467' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#013467', lineHeight: 1 }}>
              {totalRespuestas}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              respuesta{totalRespuestas !== 1 ? 's' : ''} recibida{totalRespuestas !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {preguntas.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={6}>
            No hay preguntas de selección para mostrar resultados.
          </Typography>
        )}

        {preguntas.map((p, idx) => {
          const chartData = {
            labels: p.opciones.map(o => o.texto),
            datasets: [{
              data: p.opciones.map(o => o.votos),
              backgroundColor: p.opciones.map(o => o.color),
              borderRadius: 4,
              barThickness: 22,
            }],
          };

          const chartOptions = {
            indexAxis: 'y' as const,
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => `${ctx.raw} voto${ctx.raw !== 1 ? 's' : ''}`,
                },
              },
            },
            scales: {
              x: {
                beginAtZero: true,
                ticks: { precision: 0 },
                grid: { color: '#f1f5f9' },
              },
              y: { grid: { display: false } },
            },
          };

          return (
            <Box key={p.pregunta} sx={{
              backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 2,
              boxShadow: '0 2px 8px rgba(1,52,103,0.08)',
            }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50%',
                  backgroundColor: '#eef2ff', color: '#4338ca',
                  fontSize: 12, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {idx + 1}
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                    {p.texto}
                  </Typography>
                  <Chip
                    label={TIPO_LABELS[p.tipo] || p.tipo}
                    size="small"
                    sx={{
                      height: 20, fontSize: 10, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.03em',
                      backgroundColor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe',
                      border: '1px solid',
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, ml: '38px', mb: 1.5 }}>
                {p.opciones.map(o => {
                  const pct = p.totalVotos > 0 ? Math.round((o.votos / p.totalVotos) * 100) : 0;
                  return (
                    <Box key={o.opcion} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: '2px',
                        backgroundColor: o.color, flexShrink: 0,
                      }} />
                      <Typography variant="caption" sx={{ color: '#475569' }}>
                        {o.texto}: {o.votos} ({pct}%)
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ ml: '38px', height: Math.max(80, p.opciones.length * 36), maxWidth: 460 }}>
                <Bar data={chartData} options={chartOptions} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
